import { getTemplateFields } from "./emailThumbnail.template";
import { CrmAdapter, EmailThumbnailConfig } from "./emailThumbnail.types";
import { EmailThumbnailDiagnostic } from "./emailThumbnail.validation";

export type CopyReadiness = "ready" | "unsaved" | "blocked";

export type EditorRevisionState = {
  revision: number;
  savedRevision: number;
  savingRevision: number | null;
  error: string | null;
};

export type SaveRequest = {
  projectId: string;
  token: number;
  revision: number;
};

export const isCurrentSaveRequest = (
  request: SaveRequest,
  selectedProjectId: string | undefined,
  activeToken: number | null
): boolean =>
  request.projectId === selectedProjectId && request.token === activeToken;

const templateFields = (template: string | undefined): string[] => {
  if (!template) return [];
  try {
    return getTemplateFields(template);
  } catch (_error) {
    return [];
  }
};

export const getUsedSchemaFields = (
  config: EmailThumbnailConfig,
  schemaFields: string[]
): string[] => {
  const used = new Set([
    ...templateFields(config.headline.template),
    ...templateFields(config.supportingLine?.template),
    ...templateFields(config.destinationUrl),
    ...(config.sizingStrategy === "precomputed-bucket" && config.bucketField
      ? [config.bucketField]
      : []),
  ]);

  return schemaFields.filter((field) => used.has(field));
};

export const createDefaultMappings = (
  fields: string[],
  adapter: CrmAdapter,
  current: Record<string, string> = {}
): Record<string, string> =>
  fields.reduce<Record<string, string>>((mappings, field) => {
    mappings[field] = Object.prototype.hasOwnProperty.call(current, field)
      ? current[field]
      : adapter.renderField(field);
    return mappings;
  }, {});

export const reconcileFieldMappings = (
  config: EmailThumbnailConfig,
  schemaFields: string[],
  adapter: CrmAdapter
): Record<string, string> => {
  try {
    getTemplateFields(config.headline.template);
    getTemplateFields(config.supportingLine?.template || "");
    getTemplateFields(config.destinationUrl);
  } catch (_error) {
    return { ...config.fieldMappings };
  }

  return createDefaultMappings(
    getUsedSchemaFields(config, schemaFields),
    adapter,
    config.fieldMappings
  );
};

const LONG_COPY_BY_NAME: Record<string, string> = {
  firstname: "Alexandria Catherine Montgomery-Wellington",
  company: "International Customer Experience Collective",
};

export const applyLongCopySamples = (
  fields: string[],
  current: Record<string, string>
): Record<string, string> =>
  fields.reduce<Record<string, string>>(
    (samples, field) => {
      const leafName =
        field.split(".").pop()?.toLowerCase() || field.toLowerCase();
      samples[field] =
        LONG_COPY_BY_NAME[leafName] ||
        "A deliberately long personalization value for email preview";
      return samples;
    },
    { ...current }
  );

export const getCopyReadiness = ({
  diagnostics,
  dirty,
  saved,
}: {
  diagnostics: EmailThumbnailDiagnostic[];
  dirty: boolean;
  saved: boolean;
}): CopyReadiness => {
  if (diagnostics.some((diagnostic) => diagnostic.severity === "error")) {
    return "blocked";
  }
  if (dirty || !saved) return "unsaved";
  return "ready";
};

export const createEditorRevisionState = (): EditorRevisionState => ({
  revision: 0,
  savedRevision: 0,
  savingRevision: null,
  error: null,
});

export const markEditorDirty = (
  state: EditorRevisionState
): EditorRevisionState => ({
  ...state,
  revision: state.revision + 1,
  error: null,
});

export const beginSave = (state: EditorRevisionState): EditorRevisionState => ({
  ...state,
  savingRevision: state.revision,
  error: null,
});

export const resolveSave = (
  state: EditorRevisionState,
  savedRevision: number
): EditorRevisionState => ({
  ...state,
  savedRevision,
  savingRevision: null,
  error: null,
});

export const failSave = (
  state: EditorRevisionState,
  error: string
): EditorRevisionState => ({
  ...state,
  savingRevision: null,
  error,
});
